/** Neutral copy shown after OTP request — does not reveal whether the account exists. */
export const MAGIC_LINK_SENT_MESSAGE =
  "If an account exists for this email, you'll receive a magic link shortly. Check your inbox.";

/**
 * Returns true when the OTP response should show the generic confirmation
 * rather than a Supabase error that could enable email enumeration.
 */
export function shouldConfirmMagicLinkSent(error) {
  if (!error) return true;
  const status = error.status ?? error.code;
  if (status === 429) return false;
  if (typeof status === "number" && status >= 500) return false;
  return true;
}

export function magicLinkErrorMessage(error) {
  if ((error?.status ?? 0) === 429) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  return "Something went wrong. Please try again later.";
}

/**
 * Plain copy for a failed password sign-in (null when there is no error). Never echoes Supabase's
 * raw message, which could hint at whether the email exists. Only a credential rejection blames
 * the password: `invalid_credentials`, or a bare 400 from an older GoTrue that sends no code. An
 * unconfirmed account says so, since retrying can't fix it. A rate limit, outage, other 400 or
 * thrown fetch says to try again instead.
 */
export function passwordSignInErrorMessage(error) {
  if (!error) return null;
  if (error.code === "email_not_confirmed") {
    return "This account's email is not confirmed yet. Ask your Audiohook contact to confirm it.";
  }
  const isCredentialError =
    error.code === "invalid_credentials" || (error.status === 400 && !error.code);
  return isCredentialError
    ? "That email and password didn't match."
    : "We couldn't sign you in right now. Please try again in a moment.";
}

const AUTH_QUERY_PARAMS = new Set([
  "code",
  "state",
  "access_token",
  "refresh_token",
  "expires_in",
  "expires_at",
  "token_type",
  "type",
  "token_hash",
  "error",
  "error_description",
  "error_code",
]);

const AUTH_HASH_PARAMS = new Set([
  "access_token",
  "refresh_token",
  "expires_in",
  "expires_at",
  "token_type",
  "type",
  "provider_token",
  "provider_refresh_token",
]);

function filteredSearch(search) {
  const params = new URLSearchParams(search);
  let changed = false;
  for (const key of [...params.keys()]) {
    if (AUTH_QUERY_PARAMS.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  if (!changed) return search;
  const next = params.toString();
  return next ? `?${next}` : "";
}

function filteredHash(hash) {
  if (!hash || hash === "#") return hash;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return "";
  const params = new URLSearchParams(raw);
  let changed = false;
  for (const key of [...params.keys()]) {
    if (AUTH_HASH_PARAMS.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  if (!changed) return hash;
  const next = params.toString();
  return next ? `#${next}` : "";
}

/** Remove auth tokens from the URL after Supabase establishes a session. */
export function stripAuthParamsFromUrl() {
  const { hash, search, pathname } = window.location;
  const nextSearch = filteredSearch(search);
  const nextHash = filteredHash(hash);
  if (nextSearch === search && nextHash === hash) return;
  window.history.replaceState({}, "", pathname + nextSearch + nextHash);
}

const AUTH_ERROR_CODE_MESSAGES = {
  otp_expired: "This sign-in link has expired. Request a new one below.",
  access_denied: "This sign-in link is no longer valid. Request a new one below.",
};

/**
 * Reads a GoTrue redirect error (?error=... or #error=...) straight off the
 * URL, e.g. when a magic link is clicked after it expired or was already used.
 */
export function readAuthUrlError() {
  const { search, hash } = window.location;
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const errorCode = searchParams.get("error_code") || hashParams.get("error_code");
  const error = searchParams.get("error") || hashParams.get("error");
  if (!error && !errorCode) return null;
  if (errorCode && AUTH_ERROR_CODE_MESSAGES[errorCode]) return AUTH_ERROR_CODE_MESSAGES[errorCode];
  return "This sign-in link didn't work. Request a new one below.";
}

/** True when the URL carries a PKCE `code` param, i.e. a magic-link/OAuth redirect is in flight. */
export function hasPendingAuthCode() {
  return new URLSearchParams(window.location.search).has("code");
}

/**
 * Shown when a `code` param resolved to no session and no explicit GoTrue
 * error — the code verifier is missing client-side, almost always because the
 * link was opened in a different browser/device than the one that requested it.
 */
export const MAGIC_LINK_MISMATCH_MESSAGE =
  "This sign-in link couldn't be completed in this browser. Open it in the same browser and device you requested it from, or request a new one below.";

/** Shown when verifying the emailed token_hash fails (expired, already used, or scanned). */
export const MAGIC_LINK_CONFIRM_ERROR_MESSAGE =
  "This sign-in link has expired or was already used. Request a new one below.";

const OTP_TYPES = new Set(["email", "magiclink", "signup", "invite", "recovery", "email_change"]);

/** Reads the `token_hash` (and OTP `type`) an emailed magic link carries. */
export function readTokenHashParams() {
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get("token_hash");
  if (!tokenHash) return null;
  const type = params.get("type");
  return { tokenHash, type: OTP_TYPES.has(type) ? type : "email" };
}

export const MAGIC_LINK_PENDING_KEY = "cpa_ui_magic_link_pending";
export const MAGIC_LINK_PENDING_TTL_MS = 60 * 60 * 1000; // matches the OTP expiry

/** Records that this browser just requested a magic link for `email`. */
export function markMagicLinkPending(email) {
  try {
    localStorage.setItem(
      MAGIC_LINK_PENDING_KEY,
      JSON.stringify({ email: String(email ?? "").trim().toLowerCase(), at: Date.now() }),
    );
  } catch {
    // Storage unavailable: the confirm button remains the fallback.
  }
}

/**
 * Returns `{ email }` when this browser requested a magic link less than an
 * hour ago, otherwise null. Malformed or legacy values read as null.
 */
export function readMagicLinkPending() {
  try {
    const parsed = JSON.parse(localStorage.getItem(MAGIC_LINK_PENDING_KEY));
    if (!parsed || typeof parsed.email !== "string" || !parsed.email) return null;
    const age = Date.now() - Number(parsed.at);
    if (!(Number(parsed.at) > 0 && age >= 0 && age < MAGIC_LINK_PENDING_TTL_MS)) return null;
    return { email: parsed.email };
  } catch {
    return null;
  }
}

export function clearMagicLinkPending() {
  try {
    localStorage.removeItem(MAGIC_LINK_PENDING_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Shown when verifyOtp fails with a retryable error (network, 429, 5xx); the link is kept. */
export const MAGIC_LINK_NETWORK_ERROR_MESSAGE =
  "We couldn't reach the server. Check your connection and try again.";

/** Shown when an auto-verified link belongs to a different account than the one requested here. */
export const MAGIC_LINK_ACCOUNT_MISMATCH_MESSAGE =
  "This sign-in link was for a different account. Request a new one below.";
